import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Connects to data-controller="properties-sidepanel"
//
// Manages the properties sidepanel for V3 report designer
// Opens/closes panel and switches between sheet/column properties
//
export default class extends Controller {
  static targets = [
    "panel",
    "panelTitle",
    "sheetCard",
    "columnItem",
    "sheetProperties",
    "columnProperties",
    "columnTitle",
    "emptyState",
    "content",
    "templates",
    "centerPanel",
    "manageSheetsSection"
  ]

  static values = {
    sheetId: Number
  }

  connect() {
    // Cache stores objects with { html: string, values: Map<propertyName, value> }
    this.columnPropertiesCache = new Map()
    this.currentColumnId = null
    this.showSection("empty")

    // Listen for column removal events
    this.handleColumnRemoved = this.handleColumnRemoved.bind(this)
    this.element.addEventListener('column-removed', this.handleColumnRemoved)

    // Initialize sheet parameters on page load to ensure they're saved even if
    // the user never opens the sheet settings panel
    // Use a small delay to ensure the DOM is fully rendered
    setTimeout(() => {
      this.initializeSheetParameters()
    }, 100)

    // Automatically open sheet properties panel when first navigating to a sheet
    // but only if the report has no columns (when the sheet card is present, meaning we're on the sheet editor page)
    if (this.hasSheetCardTarget) {
      setTimeout(() => {
        // Only auto-open if there are no columns in the report
        if (this.columnItemTargets.length === 0) {
          this.selectSheet()
        }
      }, 150)
    }
  }

  disconnect() {
    // Clean up event listener
    this.element.removeEventListener('column-removed', this.handleColumnRemoved)
  }

  handleColumnRemoved(event) {
    const removedColumnId = String(event.detail.columnId)

    // If the removed column's properties are currently open, close the panel
    if (this.currentColumnId === removedColumnId) {
      this.closePanel()
      this.currentColumnId = null
    }
  }

  initializeSheetParameters() {
    // Find all parameter selects in the sheet properties section
    const parameterSelects = this.element.querySelectorAll('[data-sheet-property^="parameter_"]')
    
    parameterSelects.forEach((select) => {
      const propertyName = select.dataset.sheetProperty
      
      if (!propertyName || !propertyName.startsWith('parameter_')) {
        return
      }
      
      const paramName = propertyName.replace('parameter_', '')
      const hiddenField = document.querySelector(`input[data-sheet-data="parameter_${paramName}"]`)
      
      if (hiddenField) {
        // Always sync the hidden field to match the visible select's current value
        // Even if select.value is empty, we should sync it
        const newValue = select.value || select.options[0]?.value || ''
        if (newValue !== hiddenField.value) {
          hiddenField.value = newValue
          
          // Trigger autosave to persist this change
          const autoSaveEvent = new CustomEvent('report-autosave:schedule', {
            bubbles: true,
            detail: { source: 'properties-sidepanel-init', parameter: paramName }
          })
          hiddenField.dispatchEvent(autoSaveEvent)
        }
      }
    })
  }

  openPanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add("open")
    }
    if (this.hasCenterPanelTarget) {
      this.centerPanelTarget.classList.add("properties-open")
    }
  }

  closePanel(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("open")
      this.deselectAll()
    }
    if (this.hasCenterPanelTarget) {
      this.centerPanelTarget.classList.remove("properties-open")
    }
  }

  selectSheet(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.columnItemTargets.forEach(item => item.classList.remove("selected"))

    if (this.hasSheetCardTarget) {
      this.sheetCardTarget.classList.add("selected")
    }

    if (this.hasPanelTitleTarget) {
      // Use translated sheet settings title from data attribute or fallback
      const translatedTitle = this.element.getAttribute('data-sheet-settings-title') || "הגדרות גיליון"
      this.panelTitleTarget.textContent = translatedTitle
    }

    this.showSection("sheet")
    this.openPanel()

    // Initialize sheet parameters after showing the panel
    // This ensures select elements are visible and have their default browser selection
    setTimeout(() => {
      this.initializeSheetParameters()
    }, 10)
  }

  showManageSheets(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    logger.log('[Properties Sidepanel] Showing manage sheets panel')

    // Deselect all items
    this.columnItemTargets.forEach(item => item.classList.remove("selected"))
    if (this.hasSheetCardTarget) {
      this.sheetCardTarget.classList.remove("selected")
    }

    // Update panel title
    if (this.hasPanelTitleTarget) {
      this.panelTitleTarget.textContent = "ניהול גיליונות"
    }

    // Show manage sheets section
    this.showSection("manage_sheets")
    this.openPanel()
  }

  selectColumn(event) {
    if (event) {
      event.preventDefault()
    }

    const columnElement = event.currentTarget

    if (!columnElement) {
      return
    }

    const columnId = String(event.params.columnId)

    if (!columnId || columnId === 'undefined') {
      return
    }

    // Store current column ID for removal
    this.currentColumnId = columnId

    if (this.hasSheetCardTarget) {
      this.sheetCardTarget.classList.remove("selected")
    }

    this.columnItemTargets.forEach(item => item.classList.remove("selected"))
    columnElement.classList.add("selected")

    // Update panel title with column name
    const columnTitle = columnElement.querySelector(".column-title")?.textContent?.trim() || "Column"
    if (this.hasPanelTitleTarget) {
      this.panelTitleTarget.textContent = columnTitle
    }

    // Show section
    this.showSection("column")
    this.openPanel()

    this.loadColumnPropertiesFromTemplate(columnId, columnElement)
  }

  showColumnProperties(title) {
    if (this.hasPanelTitleTarget) {
      this.panelTitleTarget.textContent = title
    }
    if (this.hasColumnTitleTarget) {
      this.columnTitleTarget.textContent = title
    }

    this.showSection("column")
  }

  showSection(section) {
    if (this.hasSheetPropertiesTarget) {
      this.sheetPropertiesTarget.classList.toggle("d-none", section !== "sheet")
    }

    if (this.hasColumnPropertiesTarget) {
      this.columnPropertiesTarget.classList.toggle("d-none", section !== "column")
    }

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("d-none", section !== "empty")
    }

    if (this.hasManageSheetsSectionTarget) {
      this.manageSheetsSectionTarget.classList.toggle("d-none", section !== "manage_sheets")
    }
  }

  deselectAll() {
    if (this.hasSheetCardTarget) {
      this.sheetCardTarget.classList.remove("selected")
    }

    this.columnItemTargets.forEach(item => item.classList.remove("selected"))
    this.showSection("empty")
  }

  removeCurrentColumn(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (!this.currentColumnId) {
      logger.warn("[Properties Sidepanel] No current column ID to remove")
      return
    }

    // Find the column element in the selected columns list
    const columnElement = document.querySelector(`#selected_column_${this.currentColumnId}`)

    if (!columnElement) {
      logger.warn(`[Properties Sidepanel] Column element not found for ID: ${this.currentColumnId}`)
      return
    }

    // Find the column-editor-v3 controller on the element
    const columnEditorController = this.application.getControllerForElementAndIdentifier(
      columnElement,
      "column-editor-v3"
    )

    if (!columnEditorController) {
      logger.warn(`[Properties Sidepanel] Column editor controller not found for ID: ${this.currentColumnId}`)
      return
    }

    // Call the removeColumn method
    columnEditorController.removeColumn(event)

    // Close the panel and clear current column ID
    this.closePanel()
    this.currentColumnId = null
  }

  clearCache() {
    this.columnPropertiesCache.clear()
  }

  loadColumnPropertiesFromTemplate(columnId, columnElement) {
    const columnType = columnElement.dataset.itemType
    
    const template = this.getTemplate(columnType)
    if (!template) {
      return
    }
    
    const columnData = this.extractColumnData(columnElement)
    const propertyForm = this.populateTemplate(template, columnData, columnId)
    
    if (this.hasColumnPropertiesTarget) {
      this.columnPropertiesTarget.innerHTML = propertyForm
      
      const newContent = this.columnPropertiesTarget
      const controllersToConnect = newContent.querySelectorAll('[data-controller]')
      
      controllersToConnect.forEach((el) => {
        const controllers = el.dataset.controller
        
        // Special handling for datepicker - DON'T disconnect/reconnect
        if (controllers.includes('datepicker')) {
          return
        }
        
        delete el.dataset.controller

        setTimeout(() => {
          el.dataset.controller = controllers
        }, 0)
      })

      this.initializeColumnPreview(newContent, columnData)
    }
  }

  initializeColumnPreview(container, columnData) {
    const previewHeader = container.querySelector('[data-column-preview="header"]')
    const previewData = container.querySelector('.preview-data-cell')

    if (!previewHeader || !previewData) {
      return
    }

    // Set header cell content and colors
    previewHeader.textContent = columnData.title || ''
    previewHeader.style.backgroundColor = columnData.header_background_color || '#ffffff'
    previewHeader.style.color = columnData.header_text_color || '#000000'

    // Set data cell colors
    previewData.style.backgroundColor = columnData.column_background_color || '#ffffff'
    previewData.style.color = columnData.column_text_color || '#000000'
  }
  
  getTemplate(columnType) {
    if (!this.hasTemplatesTarget) {
      logger.error("[Properties Sidepanel] Templates container not found")
      return null
    }
    
    // Extract type name: "ReportColumn::Formula" => "Formula"
    const typeName = columnType.split('::').pop() || 'Column'
    
    const template = this.templatesTarget.querySelector(`[data-column-type="${typeName}"]`)
    if (!template) {
      logger.warn(`[Properties Sidepanel] Template not found for "${typeName}", falling back to "Column"`)
      return this.templatesTarget.querySelector('[data-column-type="Column"]')
    }
    
    return template.cloneNode(true)
  }
  
  /**
   * Sets the value of an element AND ensures it persists through outerHTML/cloning
   * This is critical because JavaScript properties (like .value, .checked) are lost
   * when elements are cloned or serialized, but HTML attributes/textContent persist.
   *
   * @param {HTMLElement} element - The element to set value on
   * @param {*} value - The value to set
   */
  setPersistentValue(element, value) {
    const tagName = element.tagName.toLowerCase()
    const inputType = element.type?.toLowerCase()

    if (tagName === 'input' && inputType === 'checkbox') {
      // Checkbox: set both .checked property and 'checked' attribute
      const shouldCheck = (value === 'true' || value === true || value === 'checked')
      element.checked = shouldCheck
      if (shouldCheck) {
        element.setAttribute('checked', 'checked')
      } else {
        element.removeAttribute('checked')
      }
    } else if (tagName === 'textarea') {
      // Textarea: set both .value property and textContent
      element.value = value || ''
      element.textContent = value || ''
    } else if (tagName === 'select') {
      // Select: set .value property and 'selected' attribute on matching option
      element.value = value || ''
      Array.from(element.options).forEach(option => {
        if (String(option.value) === String(value)) {
          option.setAttribute('selected', 'selected')
        } else {
          option.removeAttribute('selected')
        }
      })
    } else if (tagName === 'input') {
      // Regular input: set both .value property and 'value' attribute
      element.value = value || ''
      element.setAttribute('value', value || '')
    } else {
      // Fallback: just set value property
      element.value = value || ''
    }
  }

  extractColumnData(columnElement) {
    const columnId = columnElement.dataset.columnId || columnElement.id
    logger.log(`[Properties Sidepanel] extractColumnData for column: ${columnId}`)

    const inputs = columnElement.querySelectorAll('input[data-column-data]')
    const data = {}

    inputs.forEach((input) => {
      const propertyName = input.dataset.columnData
      const value = input.value || ''
      data[propertyName] = value
      if (propertyName === 'locked' || propertyName === 'title') {
        logger.log(`[Properties Sidepanel] Extracted ${propertyName}="${value}" from hidden field`)
      }
    })
    
    const paramInputs = columnElement.querySelectorAll('input[data-column-parameter], select[data-column-parameter]')
    if (paramInputs.length > 0) {
      data.parameters = []
      paramInputs.forEach((input) => {
        const paramName = input.dataset.columnParameter
        const paramType = input.dataset.columnParameterType
        const paramValue = input.value || ''
        
        const paramData = {
          name: paramName,
          type: paramType,
          value: paramValue,
          inputName: input.name,
          label: input.dataset.columnParameterLabel || paramName
        }
        
        // For select elements, include options with labels
        if (input.tagName === 'SELECT') {
          paramData.options = Array.from(input.options).map(opt => ({
            value: opt.value,
            label: opt.textContent
          }))
        } else if (input.dataset.columnParameterOptions) {
          // For hidden inputs with options data attribute
          try {
            const optionsData = JSON.parse(input.dataset.columnParameterOptions)
            // Try to parse option labels if provided
            const optionLabels = input.dataset.columnParameterOptionLabels ?
              JSON.parse(input.dataset.columnParameterOptionLabels) : null

            paramData.options = optionsData.map((optValue, idx) => ({
              value: optValue,
              label: optionLabels ? optionLabels[idx] : optValue
            }))

            // Include option_types for format code auto-update (e.g., start_date: 'date')
            if (input.dataset.columnParameterOptionTypes) {
              paramData.optionTypes = JSON.parse(input.dataset.columnParameterOptionTypes)
            }
          } catch (e) {
            console.error('[Properties Sidepanel] Failed to parse parameter options:', e)
          }
        }
        
        data.parameters.push(paramData)
      })
    }
    
    return data
  }
  
  populateTemplate(template, data, columnId) {
    template.dataset.columnPropertiesEditorColumnIdValue = columnId

    const titlePlaceholder = template.querySelector('[data-property-placeholder="title"]')
    if (titlePlaceholder && data.title) {
      titlePlaceholder.textContent = data.title
    }

    // Populate icon and original column name (translated)
    const iconWrapper = template.querySelector('[data-column-type-icon-wrapper]')
    const iconElement = template.querySelector('[data-column-icon-element]')
    const columnNameElement = template.querySelector('[data-original-column-name]')

    // Get type class from column data (for colored icon background)
    const typeClass = data.type_class || 'type-data'

    if (iconWrapper) {
      // Update icon wrapper classes to match the column type
      iconWrapper.className = `column-type-icon me-2 ${typeClass}`
    }

    if (iconElement && data.column_icon) {
      // Update icon
      iconElement.className = `fas fa-${data.column_icon}`
    }

    // Populate column name with tooltip
    const displayName = data.original_title || (data.type ? data.type.split('::').pop() : 'Column')

    if (columnNameElement) {
      columnNameElement.textContent = displayName
      columnNameElement.setAttribute('title', displayName)

      // Initialize Bootstrap tooltip if available
      if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
        new bootstrap.Tooltip(columnNameElement)
      }
    }

    // Populate ALL elements with data-column-property using the universal helper
    // This handles inputs, textareas, checkboxes, selects - everything!
    const propertyElements = template.querySelectorAll('[data-column-property]')

    propertyElements.forEach((element) => {
      const propertyName = element.dataset.columnProperty

      // Handle _text variants (for color inputs that sync with color pickers)
      if (propertyName.endsWith('_text')) {
        // Use regex with $ anchor to only remove _text at the END of the string
        // This is critical because property names like "header_text_color_text" contain "_text" twice
        const baseProperty = propertyName.replace(/_text$/, '')
        if (data[baseProperty] !== undefined) {
          this.setPersistentValue(element, data[baseProperty])
        }
      } else if (data[propertyName] !== undefined) {
        this.setPersistentValue(element, data[propertyName])

        // Log checkboxes for debugging
        if (element.tagName === 'INPUT' && element.type === 'checkbox') {
          logger.log(`[Properties Sidepanel] Set ${propertyName} checkbox to: ${element.checked} (data value was: "${data[propertyName]}")`)
        }
      }
    })

    if (data.parameters && data.parameters.length > 0) {
      const parametersContainer = template.querySelector('[data-column-parameters-container]')
      if (parametersContainer) {
        parametersContainer.innerHTML = ''
        data.parameters.forEach((param) => {
          const formGroup = document.createElement('div')
          formGroup.className = 'form-group mb-3'

          const label = document.createElement('label')
          label.className = 'form-control-label'
          label.textContent = param.label || param.name
          formGroup.appendChild(label)

          let input
          if (param.type === 'date') {
            const dateWrapper = document.createElement('div')
            dateWrapper.className = 'flex-grow-1'
            dateWrapper.dataset.controller = 'datepicker'
            dateWrapper.dataset.datepickerModeValue = 'date'
            dateWrapper.dataset.datepickerFormatValue = 'd-m-Y'
            dateWrapper.dataset.datepickerLocaleValue = 'he'

            input = document.createElement('input')
            input.type = 'text'
            input.className = 'form-control'
            input.placeholder = 'dd-mm-yyyy'
            this.setPersistentValue(input, param.value)
            input.dataset.columnParameter = param.name
            input.dataset.columnParameterType = param.type
            input.dataset.parameterName = param.inputName
            input.dataset.action = 'change->column-properties-editor#updateParameter'

            dateWrapper.appendChild(input)

            // Create input-group for icon
            const inputGroup = document.createElement('div')
            inputGroup.className = 'input-group'
            inputGroup.appendChild(dateWrapper)

            // Create icon span (after input)
            const iconSpan = document.createElement('span')
            iconSpan.className = 'input-group-text'
            iconSpan.innerHTML = '<i class="fa fa-calendar"></i>'
            inputGroup.appendChild(iconSpan)

            formGroup.appendChild(inputGroup)
          } else if (param.type === 'select' && param.options) {
            // Create select dropdown
            input = document.createElement('select')
            input.className = 'form-control'
            input.dataset.columnParameter = param.name
            input.dataset.columnParameterType = param.type
            input.dataset.parameterName = param.inputName
            input.dataset.action = 'change->column-properties-editor#updateParameter'

            // Include option_types for format code auto-update (e.g., start_date: 'date')
            // CRITICAL: Use setAttribute to persist in outerHTML (see ADDING_REPORT_COLUMN_PARAMETERS.md)
            if (param.optionTypes) {
              const optionTypesJson = JSON.stringify(param.optionTypes)
              input.dataset.columnParameterOptionTypes = optionTypesJson
              input.setAttribute('data-column-parameter-option-types', optionTypesJson)
            }

            // Add options
            param.options.forEach((opt, index) => {
              const option = document.createElement('option')
              const optValue = typeof opt === 'object' ? opt.value : opt
              const optLabel = typeof opt === 'object' ? opt.label : opt.toString().split('_').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ')

              option.value = optValue
              option.textContent = optLabel
              input.appendChild(option)
            })

            // Set the selected value using helper (handles attribute setting)
            this.setPersistentValue(input, param.value)

            formGroup.appendChild(input)
          } else if (param.type === 'select_array') {
            // Create textarea for multi-value options (newline-separated)
            const textarea = document.createElement('textarea')
            textarea.className = 'form-control'
            textarea.rows = 5
            textarea.placeholder = 'אפשרות אחת בכל שורה / One option per line'

            // Parse JSON array to newline-separated text
            let optionsArray = []
            try {
              const parsed = JSON.parse(param.value || '[]')
              // Ensure it's actually an array
              optionsArray = Array.isArray(parsed) ? parsed : []
            } catch (e) {
              optionsArray = []
            }
            const textValue = optionsArray.join('\n')

            // Use helper to set both .value and textContent
            this.setPersistentValue(textarea, textValue)

            textarea.dataset.columnParameter = param.name
            textarea.dataset.columnParameterType = param.type
            textarea.dataset.parameterName = param.inputName
            textarea.dataset.action = 'input->column-properties-editor#updateParameter'

            formGroup.appendChild(textarea)
          } else {
            // Fallback: generic text input
            input = document.createElement('input')
            input.type = 'text'
            input.className = 'form-control'
            this.setPersistentValue(input, param.value)
            input.dataset.columnParameter = param.name
            input.dataset.columnParameterType = param.type
            input.dataset.parameterName = param.inputName
            input.dataset.action = 'input->column-properties-editor#updateParameter'
            formGroup.appendChild(input)
          }

          parametersContainer.appendChild(formGroup)
        })
      }
    }

    // Set column ID on the duplicate button so it can find the correct column item
    const duplicateButton = template.querySelector('[data-action*="column-actions#duplicateColumn"]')
    if (duplicateButton) {
      duplicateButton.dataset.columnActionsColumnIdValue = columnId
    }

    return template.outerHTML
  }
}

